//changed due to package upgrade
import { useEffect, useMemo, useState } from 'react';
import {
  alpha,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Modal,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import PropTypes from 'prop-types';
import productThemes from '@projectK/constants/ProductThemes';
import jwtAxios from '@projectK/services/auth/JWT';
import { useInfoViewActionsContext } from '@projectK/context';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import EditIcon from '@mui/icons-material/Edit';
import ImageUpload from './ImageUpload';
import AppLoader from '@projectK/components/AppLoader';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import { useApplicationSettings } from '@projectK/context/VerbiageContextProvider';
import AppScrollbar from '@projectK/components/AppScrollbar';
import CloseIcon from '@mui/icons-material/Close';
import { encrypt } from '@projectK/modules/apps/encryption';
import { ProductViewMain } from '@projectK/MainComponents';
import {
  IsDateFormatString,
  timeZoneDate,
} from '@projectK/modules/apps/DateFormat';
import {
  useRolePermissions,
  hasPermission,
  isParent,
} from '@projectK/context/RolesProvider';
import WaitlistModal from './WaitlistModal';
import {
  ProductProfileFieldsGrid,
  validateProductProfileFields,
  serializeProductProfileFieldsPayload,
  mapUserProfileDataToFormValues,
  getProfileFieldsStructureKey,
} from '../../../profileFields';
const renderUpdateImage = ({
  product,
  selectedTheme,
  handleEditClick,
  rolePermissions,
}) => {
  return (
    <Grid size={{ xs: 12, md: 4, sm: 5 }} style={{ position: 'relative' }}>
      {product?.displaySettings?.product_image ? (
        <>
          <div className='p-img'>
            <img
              src={`${process.env.NX_CLOUD_FRONT_URL}/${product?.displaySettings?.product_image}`}
              alt='product'
            />
          </div>
          {hasPermission(
            rolePermissions,
            'Product Setup - Manage Products',
            'update',
          ) && (
            <Button
              variant='contained'
              color='primary'
              style={{
                minWidth: 100,
                maxHeight: 30,
                position: 'absolute',
                top: '10px',
                left: '10px',
                backgroundColor: selectedTheme
                  ? selectedTheme.primary.main
                  : '#2196f3',
                paddingLeft: 0,
              }}
              className='edit-icon'
              onClick={handleEditClick}
            >
              <IconButton
                size='small'
                className='icon-btn icon-btn-eye'
                sx={{ color: 'white' }}
              >
                <EditIcon />
              </IconButton>
              Update Image
            </Button>
          )}
        </>
      ) : (
        <>
          <div className='p-img'>
            <img src='/assets/images/default-product.png' alt='product' />
          </div>
          {hasPermission(
            rolePermissions,
            'Product Setup - Manage Products',
            'update',
          ) && (
            <Button
              variant='contained'
              color='primary'
              style={{
                minWidth: 100,
                maxHeight: 30,
                position: 'absolute',
                top: '10px',
                left: '10px',
                backgroundColor: selectedTheme
                  ? selectedTheme.primary.main
                  : '#2196f3',
                paddingLeft: 0,
              }}
              className='edit-icon'
              onClick={handleEditClick}
            >
              <IconButton
                size='small'
                className='icon-btn icon-btn-eye'
                sx={{ color: 'white' }}
              >
                <EditIcon />
              </IconButton>
              Update Image
            </Button>
          )}
        </>
      )}
    </Grid>
  );
};
const renderCreditInfo = (data) => {
  if (data.credit_option === 0) {
    return (
      <div>
        <p></p>
      </div>
    );
  }

  if (data.check_here_for_unlimited_credits) {
    return <div>∞ Credits</div>;
  }

  return <div>{data.credit_amount} Credits</div>;
};
const getPaymentAmount = (data) => {
  if (data.is_deposit) return data.deposit_amount;
  if (data?.monthly_price === '0.00') return data?.purchase_price;
  return data?.monthly_price;
};
const getPaymentLabel = (data) => {
  if (data.is_deposit && data?.payment_plan_id) {
    return data.payment_plan_id === 1
      ? `Monthly Payment: $${data.remaining_amount}`
      : `Payments until ${data.payment_plan_id} Months: $${data.remaining_amount}`;
  }
  return `Total: $${data.purchase_price}`;
};

const renderPurchaseOptions = ({
  purchaseOptions,
  isProductLimit,
  selectedTheme,
  slug,
  onAddToCard,
  handleOpenWaitlist,
}) => {
  return (
    <Grid
      size={{ xs: 12, sm: 12, md: 12 }}
      className='purchase-options'
      id='productpurchaseoption'
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {!isProductLimit ? ( //NOSONAR
          purchaseOptions.length > 0 ? ( //NOSONAR
            <Box sx={{ marginBottom: 4 }}>
              <Typography variant='h2'>Purchase Options</Typography>
            </Box>
          ) : (
            //NOSONAR
            <Box sx={{ marginBottom: 4 }}>
              <Typography variant='h3'>
                No Purchase Options Available
              </Typography>
            </Box>
          )
        ) : (
          //NOSONAR
          <Typography
            variant='h3'
            sx={{ color: 'red', fontWeight: 'bold', textAlign: 'center' }}
          >
            This product is out of stock.
            <br />
            <Typography variant='body1'>
              Join the waitlist to be notified when it's available again.
            </Typography>
          </Typography>
        )}
        {!isProductLimit ? ( //NOSONAR
          <Box className='purchase-buttons'>
            {purchaseOptions.filter(
              (option) => option.membership_expiration_visible === 1,
            ).length > 0
              ? purchaseOptions
                  .filter(
                    (option) => option.membership_expiration_visible === 1,
                  )
                  ?.map((data, index) => {
                    return (
                      <Grid
                        key={data.id}
                        sx={{
                          display: 'block',
                          marginBottom: 5,
                          textAlign: 'center',
                        }}
                      >
                        <div>
                          <Typography
                            variant='h3'
                            sx={{ textAlign: 'center', marginBottom: 2 }}
                          >
                            <div>
                              {data.option_label}
                              {renderCreditInfo(data)}
                            </div>
                          </Typography>
                          <Button
                            variant='contained'
                            color='primary'
                            style={{
                              maxWidth: 200,
                              minWidth: 140,
                              backgroundColor: selectedTheme
                                ? selectedTheme.primary.main
                                : '#2196f3',
                              margin: 'auto',
                            }}
                            onClick={() => {
                              if (slug) {
                                window.open(
                                  `/signin?refurl=/products/${slug}`,
                                  '_blank',
                                );
                              } else {
                                onAddToCard(
                                  getPaymentAmount(data),
                                  data?.payment_plan_id,
                                  data?.id,
                                  data?.product_id,
                                  data?.is_deposit,
                                  data?.remaining_amount,
                                );
                              }
                            }}
                          >
                            {(() => {
                              if (data.is_deposit) {
                                return (
                                  <div>
                                    Deposit ${data.deposit_amount}
                                    <br />
                                    {getPaymentLabel(data)}
                                  </div>
                                );
                              } else if (data.monthly_price === '0.00') {
                                return (
                                  <span>{`$ ${data.purchase_price}`}</span>
                                );
                              } else {
                                return (
                                  <span>
                                    {data.payment_plan_label} - $
                                    {data.monthly_price}
                                  </span>
                                );
                              }
                            })()}
                          </Button>
                        </div>
                      </Grid>
                    );
                  })
              : 'No Purchase Options Available'}
          </Box>
        ) : (
          <Grid
            sx={{
              display: 'block',
              marginBottom: 5,
              textAlign: 'center',
            }}
          >
            <div>
              <Button
                variant='contained'
                color='primary'
                style={{
                  maxWidth: 200,
                  minWidth: 140,
                  backgroundColor: selectedTheme
                    ? selectedTheme.primary.main
                    : '#2196f3',
                }}
                onClick={() => {
                  if (slug) {
                    window.open(`/signin?refurl=/products/${slug}`, '_blank');
                  } else {
                    handleOpenWaitlist();
                  }
                }}
              >
                Join the Waitlist
              </Button>
            </div>
          </Grid>
        )}
      </Box>
    </Grid>
  );
};
const renderDialogForm = ({
  handleSubmit,
  membersData,
  user,
  selectedUsers,
  setSelectedUsers,
  setChildId,
  isChildIdValid,
  requiredProfileFields,
  profileFields,
  formValues,
  handleInputChange,
  formSubmitted,
  waivers,
  verbiageSettings,
  companyTheme,
  handleOpen,
  modalOpen,
  selectedWaiverData,
  handleClose,
  enteredName,
  setEnteredName,
  handleSave,
  childId,
  handleClosePopup,
  fieldErrors,
}) => {
  return (
    <form onSubmit={handleSubmit}>
      {isParent(user) &&
        membersData?.length > 0 && ( //useroletag
          <Grid size={{ xs: 12, md: 12 }}>
            <Autocomplete
              disableClearable
              options={(membersData || []).sort((a, b) =>
                (a.first_name + ' ' + a.last_name).localeCompare(
                  b.first_name + ' ' + b.last_name,
                  undefined,
                  { numeric: true, sensitivity: 'base' },
                ),
              )}
              value={selectedUsers}
              onChange={(event, newValue) => {
                setSelectedUsers(newValue);
                setChildId(newValue?.id);
              }}
              getOptionLabel={(option) =>
                `${option.first_name} ${option.last_name} - ${option.email}`
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Search Member'
                  name='member_id'
                  error={!isChildIdValid}
                  sx={{
                    mt: 4,
                  }}
                  InputProps={{// NOSONAR - MUI v6 Autocomplete still requires InputProps for dropdown anchor
                    ...params.InputProps,
                    type: 'search',
                  }}
                />
              )}
            />
          </Grid>
        )}
      {profileFields?.length > 0 && (
        <>
          <Typography variant='h3' sx={{ marginBottom: '10px' }}>
            Profile Fields
          </Typography>
          <Grid container spacing={2}>
            {profileFields?.length ? (
              <ProductProfileFieldsGrid
                profileFields={profileFields}
                formValues={formValues}
                formSubmitted={formSubmitted}
                onChange={handleInputChange}
                fieldErrors={fieldErrors}
              />
            ) : (
              <Grid size={{ xs: 12 }}>
                <p>No profile fields available</p>
              </Grid>
            )}
          </Grid>
        </>
      )}

      {waivers?.length > 0 && (
        <Typography variant='h3' sx={{ marginTop: '10px' }}>
          Waivers
        </Typography>
      )}
      {waivers?.map((waiver, index) => (
        <Card key={waiver.id} style={{ margin: '10px', width: '95%' }}>
          <CardContent>
            <Typography variant='h4'>{waiver.waiver_name}</Typography>
            <Typography color='textSecondary'>
              {waiver.signed && waiver.saveClicked
                ? `${verbiageSettings.waiver_name || 'Waiver'} signed`
                : `Click to sign ${verbiageSettings.waiver_name || 'waiver'}`}
            </Typography>
          </CardContent>
          <CardActions>
            <Button
              sx={{
                position: 'relative',
                minWidth: 100,
                ml: 2.5,
                borderColor: companyTheme
                  ? companyTheme.primary.main
                  : '#2196f3',
                '&:hover': {
                  borderColor: companyTheme
                    ? alpha(companyTheme.primary.main, 0.8)
                    : alpha('#2196f3', 0.8),
                },
                color: companyTheme ? companyTheme.primary.main : '#2196f3',
              }}
              variant={
                (waiver.signed && waiver.saveClicked) || !waiver.signed
                  ? 'outlined'
                  : 'contained'
              }
              onClick={() => handleOpen(waiver)}
              disabled={waiver.signed}
            >
              Sign {verbiageSettings.waiver_name || 'Waiver'}
            </Button>
          </CardActions>

          {modalOpen && waiver?.id === selectedWaiverData?.id && (
            <Modal
              open={modalOpen}
              onClose={() => handleClose(index)}
              aria-labelledby='modal-title'
              aria-describedby='modal-description'
            >
              <div className='waiver-pop'>
                <AppScrollbar className='waiver-scroll'>
                  <DialogTitle
                    id='modal-title'
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '18px',
                    }}
                  >
                    {selectedWaiverData?.waiver_name} Signature
                    <IconButton
                      onClick={() => handleClose(index)}
                      aria-label='close'
                    >
                      <CloseIcon />
                    </IconButton>
                  </DialogTitle>
                  <DialogContent>
                    <DialogContentText
                      id='modal-description'
                      style={{ color: '#000' }} // Set the color to a darker value
                    >
                      <div
                        dangerouslySetInnerHTML={{
                          __html:
                            selectedWaiverData?.description ||
                            'No description found',
                        }}
                      />
                    </DialogContentText>
                    <DialogContentText
                      id='modal-description'
                      sx={{ marginTop: '10px', color: '#808080' }}
                    >
                      This is a legally binding document. I am {enteredName},
                      and I agree to be legally bound by this digital signature.
                      I understand that I have the option to sign a paper copy
                      instead.
                    </DialogContentText>
                    <TextField
                      sx={{ mt: 6 }}
                      label={`Signer's Name`}
                      fullWidth
                      variant='outlined'
                      value={enteredName}
                      onChange={(e) => setEnteredName(e.target.value)}
                    />
                  </DialogContent>
                  <DialogActions>
                    <Button
                      onClick={() => handleSave(childId, selectedWaiverData)}
                      disabled={enteredName.trim() === ''}
                    >
                      Accept
                    </Button>
                  </DialogActions>
                </AppScrollbar>
              </div>
            </Modal>
          )}
        </Card>
      ))}
      <Grid size={{ xs: 12, md: 12 }}>
        <Box mt={2}>
          <Button
            type='submit'
            sx={{
              position: 'relative',
              minWidth: 100,
              ml: 2.5,
              backgroundColor: companyTheme
                ? companyTheme.primary.main
                : '#2196f3',
              '&:hover': {
                backgroundColor: companyTheme
                  ? alpha(companyTheme.primary.main, 0.8)
                  : alpha('#2196f3', 0.8),
              },
            }}
            disabled={(!isChildIdValid && isParent(user)) || waivers.length > 0}
            variant='contained'
          >
            Add Product
          </Button>
          <Button
            sx={{
              position: 'relative',
              minWidth: 100,
              ml: 2.5,
              borderColor: companyTheme ? companyTheme.primary.main : '#2196f3',
              '&:hover': {
                borderColor: companyTheme
                  ? alpha(companyTheme.primary.main, 0.8)
                  : alpha('#2196f3', 0.8),
              },
              color: companyTheme ? companyTheme.primary.main : '#2196f3',
            }}
            variant='outlined'
            onClick={handleClosePopup}
            type='button'
          >
            Cancel
          </Button>
        </Box>
      </Grid>
    </form>
  );
};
const renderEditButton = ({
  rolePermissions,
  selectedTheme,
  navigate,
  productId,
  product,
}) => {
  return (
    <Grid size={{ xs: 12, md: 8, sm: 7 }} className='details'>
      {hasPermission(
        rolePermissions,
        'Product Setup - Manage Products',
        'update',
      ) && (
        <Button
          variant='contained'
          color='primary'
          style={{
            minWidth: 100,
            alignItems: 'flex-start',
            backgroundColor: selectedTheme
              ? selectedTheme.primary.main
              : '#2196f3',
            paddingLeft: 0,
          }}
          onClick={() => navigate(`/admin/service/settings/${productId}`)}
        >
          <IconButton
            size='small'
            className='icon-btn icon-btn-eye'
            sx={{ color: 'white' }}
          >
            <EditIcon />
          </IconButton>
          Manage&nbsp; <span className='wordbreak'>{product.product_name}</span>
        </Button>
      )}
      <Box
        component='p'
        sx={{
          color: 'text.secondary',
          textAlign: 'justify',
        }}
      >
        <div
          dangerouslySetInnerHTML={{
            __html:
              product.displaySettings?.product_description ||
              'No description found',
          }}
        />
      </Box>
    </Grid>
  );
};

const isFreeProduct = (purchaseOptions) => {
  const price = purchaseOptions?.[0];
  return (
    price &&
    (price.purchase_price === '0.00' || price.purchase_price === 0) &&
    (price.monthly_price === '0.00' || price.monthly_price === 0)
  );
};

const getParentText = ({ purchaseOptions }) => {
  return isFreeProduct(purchaseOptions)
    ? 'The price for this product is $0.00. Select a child to add this product to their profile?'
    : 'The price for this product is $0.00. Select a child to add this product to your cart?';
};

const getStudentText = ({ purchaseOptions }) => {
  return isFreeProduct(purchaseOptions)
    ? 'The price for this product is $0.00. Are you sure you want to add it to your profile?'
    : 'Would you like to add this product to your cart?';
};

const ProductView = ({ product, purchaseOptions, fetchData, slug }) => {
  const { user } = useAuthUser();
  const { productId } = useParams();
  const selectedTheme = productThemes.find(
    (theme) => theme.title === product?.displaySettings?.product_theme,
  );
  const isProductLimit =
    product?.purchase_limit !== null && product?.purchase_limit !== undefined
      ? product.purchased_count >= product.purchase_limit
      : false;
  const infoViewActionsContext = useInfoViewActionsContext();
  const { verbiageSettings } = useApplicationSettings();
  const navigate = useNavigate();
  const companyTheme = CompanyTheme();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [freePurchaseOption, setFreePurchaseOption] = useState(null);
  const [freeProductId, setFreeProductId] = useState(null);
  const [membersData, setMembersData] = useState([]);
  const [childId, setChildId] = useState(null);
  const [isChildIdValid, setIsChildIdValid] = useState(false); // Add state for validation
  const [loading, setLoading] = useState(true);
  const [profileFields, setProfileFields] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [requiredProfileFields, setRequiredProfileFields] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState(null);
  const [waivers, setWaivers] = useState([]);
  const { rolePermissions } = useRolePermissions();
  const [isWaitlistModalOpen, setIsWaitlistModalOpen] = useState(false);
  const handleOpenWaitlist = () => {
    setIsWaitlistModalOpen(true);
  };

  const handleCloseWaitlist = () => {
    setIsWaitlistModalOpen(false);
  };

  const handleInputChange = (field, valueOrEvent) => {
    const value =
      valueOrEvent?.target !== undefined
        ? valueOrEvent.target.value
        : valueOrEvent;
    const fieldId = field?.profile_field_id;
    setFormValues((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
    if (fieldErrors[fieldId]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });
    }
  };

  const onAddToCard = async (
    price,
    payment_plan_id,
    product_purchase_id,
    product_ID,
    is_deposit,
    remaining_amount,
  ) => {//NOSONAR
    const todayDate = timeZoneDate().todayDate.toFormat('yyyy-MM-dd');
    const productExpired = product?.expiry_date
      ? (await IsDateFormatString(product?.expiry_date)).split('T')[0] <
        todayDate
      : false;

    if (productExpired) {
      infoViewActionsContext.fetchError('Product is expired');
      return;
    }
    if (product?.purchase_limit) {
      if (
        product?.purchase_limit &&
        product?.purchased_count >= product.purchase_limit
      ) {
        infoViewActionsContext.fetchError(
          'You cannot purchase this product as this product is limited.',
        );
      }
    }
    const purchaseOption = purchaseOptions.find(
      (purchaseOption) => purchaseOption.id === product_purchase_id,
    );
    const balancePaymentDue = purchaseOption?.balance_payment_due_date;
    if (balancePaymentDue) {
      const balancePaymentDueDate = balancePaymentDue
        ? (await IsDateFormatString(balancePaymentDue)).split('T')[0] <
          todayDate
        : false;
      if (balancePaymentDueDate) {
        infoViewActionsContext.fetchError(
          'You cannot purchase this product, Please contact your administartor',
        );
        return true;
      }
    } else if (is_deposit === 1 && !balancePaymentDue) {
      infoViewActionsContext.fetchError(
        'You cannot purchase this product, Please contact your administartor',
      );
      return true;
    }
    const purchaseOptionExpiryto =
      purchaseOption?.estimated_offline_purchase_to;
    const purchaseOptionExpiryfrom =
      purchaseOption?.estimated_offline_purchase_from;
    const Ispurchaseto = purchaseOptionExpiryto
      ? (await IsDateFormatString(purchaseOptionExpiryto)).split('T')[0] <
        todayDate
      : false;
    if (Ispurchaseto) {
      infoViewActionsContext.fetchError('Purchase option is expired');
      return true;
    }
    const IspurchaseFrom = purchaseOptionExpiryfrom
      ? (await IsDateFormatString(purchaseOptionExpiryfrom)).split('T')[0] >
        todayDate
      : false;
    if (IspurchaseFrom) {
      infoViewActionsContext.fetchError('Purchase option is not started');
      return true;
    }

    if (price === '0.00' || price === 0) {
      setIsPopupOpen(true);
      setFreePurchaseOption(product_purchase_id);
      setFreeProductId(product_ID);
      return;
    }
    try {
      const encrypteData = await encrypt('productview');
      // Make the POST request using jwtAxios
      await jwtAxios.post(`/cart/`, {
        product_id: product.id,
        quantity: 1,
        is_deposit: is_deposit === 1,
        remaining_amount: remaining_amount,
        item_price: price,
        ...(payment_plan_id && { payment_plan_id }),
        ...(product_purchase_id && { product_purchase_id }),
        iscartupdated: encrypteData,
        user_id: user.id,
      });
      infoViewActionsContext.showMessage(
        `${product.product_name} added to cart successfully`,
      );
      navigate('/cart');
    } catch (error) {
      infoViewActionsContext.fetchError(error?.response?.data?.error);
    }
  };

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setFormSubmitted(false);
    setFieldErrors({});
    setChildId('');
    setSelectedUsers(null);
    setWaivers([]);
  };

  const handleEditClick = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
  };

  let productName;

  if (product.product_name) {
    if (product.product_name.length > 20) {
      productName = (
        <Tooltip title={product.product_name}>
          {product.product_name.substring(0, 10) + '...'}
        </Tooltip>
      );
    } else {
      productName = product.product_name;
    }
  }

  useEffect(() => {
    fetchMembersData();
  }, []);
  const fetchMembersData = async () => {
    try {
      const response = await jwtAxios.get(`/user-members/${user?.id}`, {
        params: {
          perPage: 'all',
        },
      });
      const { data } = response;
      setMembersData(data.list);
    } catch (error) {
      setMembersData([]);
    }
  };
  // Validation logic for the TextField
  useEffect(() => {
    setIsChildIdValid(!!childId); // Simple validation: Check if childId is not null or empty
  }, [childId]);
  const parentText = getParentText({ purchaseOptions });
  const studentText = getStudentText({ purchaseOptions });

  useEffect(() => {
    if (freeProductId) {
      fetchProfileFields();
    }
  }, [freeProductId]);

  const fetchProfileFields = async () => {
    try {
      setLoading(true);
      const response = await jwtAxios.get(
        `/profile-fields/product-profile/${freeProductId}`,
      );
      const { data } = response;
      setProfileFields(data.list);
      setLoading(false);
    } catch (error) {
      setProfileFields([]);
      setLoading(false);
    }
  };

  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleSubmit = async (e) => {//NOSONAR
    e.preventDefault();
    setFormSubmitted(true);

    const errors = validateProductProfileFields(profileFields, formValues);
    setFieldErrors(errors);
    if (Object.keys(errors).length === 0) {
      try {
        const fieldsData = serializeProductProfileFieldsPayload(
          formValues,
          profileFields,
          profileFields[0]?.product_id,
          profileFields[0]?.profile_id,
        );
        if (fieldsData?.length > 0) {
          let profileResponse;
          if (isParent(user) && membersData?.length > 0 ? childId : user?.id) {
            profileResponse = await jwtAxios.post('/user-profiles', {
              user_id:
                isParent(user) && membersData?.length > 0 ? childId : user?.id,
              product_id: freeProductId,
              profile_id: profileFields[0].profile_id,
            });
          }
          const userProfileId = profileResponse?.data?.id;
          if (userProfileId) {
            // Add profile data
            await jwtAxios.post('/user-profiles-data', {
              user_profile_id: userProfileId,
              fields: fieldsData,
            });
          }
        }
        if (freePurchaseOption && freeProductId) {
          await jwtAxios.post(`/product-members/${freeProductId}`, [
            {
              credit_option_id: freePurchaseOption,
              send_invitation: false,
              // member_id: user.id,
              member_id:
                isParent(user) && membersData?.length > 0 ? childId : user?.id,
            },
          ]);
          infoViewActionsContext.showMessage(
            `${product.product_name} added to profile successfully`,
          );
          handleClosePopup();
        }
        handleClosePopup();
        setFormValues({});
        setFieldErrors({});
      } catch (error) {
        infoViewActionsContext.fetchError(error?.response?.data?.error);
      }
    }
  };

  const [enteredName, setEnteredName] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedWaiverData, setSelectedWaiverData] = useState(null);
  const userID = isParent(user) && membersData.length > 0 ? childId : user?.id;
  const handleOpen = (data) => {
    setSelectedWaiverData(data);
    setModalOpen(true);
  };

  const handleClose = () => {
    setSelectedWaiverData(null);
    setModalOpen(false);
  };
  useEffect(() => {
    if (
      isParent(user) && membersData.length > 0 && freeProductId
        ? freeProductId && childId
        : freeProductId && user?.id
    ) {
      fetchWaiversData();
    }
  }, [freeProductId, childId, membersData?.length > 0, user?.id]);
  const fetchWaiversData = async () => {
    setLoading(true);
    try {
      const response = await jwtAxios.get(
        `/product-waiver-settings/user/${freeProductId}/${
          isParent(user) && membersData.length > 0 ? childId : user?.id
        }`,
      );
      const { data } = response;
      setWaivers(data?.list);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('An error occurred while fetching waivers:', error);
    }
  };

  const handleSave = async (userId, waiverData) => {
    try {
      await jwtAxios.post('/user-product-waivers', {
        user_id: userId || user?.id,
        product_waiver_id: waiverData?.id,
        signed: true,
        product_id: waiverData?.product_id,
        signed_by: user?.actualUser ? user?.actualUser : user?.id,
        signer_name: enteredName || '',
      });
      fetchWaiversData();
      setSelectedWaiverData(null);
      setModalOpen(false);
    } catch (error) {
      console.error('An error occurred:', error);
    }
  };
  const fetchRequiredProfileFields = async () => {
    try {
      const response = await jwtAxios.get(
        `/profile-fields/product/${freeProductId}/${user?.id}`,
      );

      const allData = response?.data?.list;
      const uniqueDataSet = new Set();
      const finalData = [];

      for (const item of allData) {
        const key = `${item.profile_field_id}_${item.profile_id}_${item.user_id}`;
        if (!uniqueDataSet.has(key)) {
          uniqueDataSet.add(key);
          finalData.push(item);
        }
      }
      setRequiredProfileFields(finalData);
    } catch (error) {
      setProfileFields([]);
    }
  };
  useEffect(() => {
    if (
      isParent(user) && membersData.length > 0 && freeProductId
        ? freeProductId && childId
        : freeProductId && user?.id
    ) {
      fetchRequiredProfileFields();
    }
  }, [freeProductId, childId, membersData?.length > 0, user?.id]);
  const profileFieldsKey = useMemo(
    () => getProfileFieldsStructureKey(profileFields),
    [profileFields],
  );

  useEffect(() => {
    if (!isPopupOpen || !profileFieldsKey || !userID) {
      return undefined;
    }

    let cancelled = false;

    const loadProfileFormValues = async () => {
      try {
        const response = await jwtAxios.get(`/user-profiles-data/${userID}`);
        if (cancelled) {
          return;
        }
        setFormValues(
          mapUserProfileDataToFormValues(response.data, profileFields),
        );
      } catch (error) {
        console.error('error', error);
      }
    };

    loadProfileFormValues();

    return () => {
      cancelled = true;
    };
  }, [isPopupOpen, userID, profileFieldsKey, profileFields]);
  return (
    <>
      <ProductViewMain
        handleSave={handleSave}
        handleClose={handleClose}
        handleOpen={handleOpen}
        selectedWaiverData={selectedWaiverData}
        modalOpen={modalOpen}
        enteredName={enteredName}
        setEnteredName={setEnteredName}
        handleSubmit={handleSubmit}
        formSubmitted={formSubmitted}
        studentText={studentText}
        parentText={parentText}
        handleCloseModal={handleCloseModal}
        handleEditClick={handleEditClick}
        productName={productName}
        isEditModalOpen={isEditModalOpen}
        onAddToCard={onAddToCard}
        handleInputChange={handleInputChange}
        waivers={waivers}
        selectedUsers={selectedUsers}
        loading={loading}
        isChildIdValid={isChildIdValid}
        isPopupOpen={isPopupOpen}
        companyTheme={companyTheme}
        verbiageSettings={verbiageSettings}
        isProductLimit={isProductLimit}
        selectedTheme={selectedTheme}
        productId={productId}
        renderEditButton={renderEditButton}
        renderDialogForm={renderDialogForm}
        renderPurchaseOptions={renderPurchaseOptions}
        renderUpdateImage={renderUpdateImage}
        ImageUpload={ImageUpload}
        AppLoader={AppLoader}
        product={product}
        purchaseOptions={purchaseOptions}
        fetchData={fetchData}
        slug={slug}
        user={user}
        handleClosePopup={handleClosePopup}
        navigate={navigate}
        setSelectedUsers={setSelectedUsers}
        childId={childId}
        setChildId={setChildId}
        formValues={formValues}
        setFormValues={setFormValues}
        membersData={membersData}
        setMembersData={setMembersData}
        profileFields={profileFields}
        fieldErrors={fieldErrors}
        requiredProfileFields={requiredProfileFields}
        rolePermissions={rolePermissions}
        handleOpenWaitlist={handleOpenWaitlist}
      />
      {isWaitlistModalOpen && (
        <WaitlistModal
          open={isWaitlistModalOpen}
          onClose={handleCloseWaitlist}
          productName={product?.product_name}
          productId={product?.id}
          userId={user?.id}
          userEmail={user?.email}
          userMobile={user?.mobile}
          userName={`${user?.first_name || ''} ${user?.last_name || ''}`.trim()}
          companyTheme={companyTheme}
          infoViewActionsContext={infoViewActionsContext}
        />
      )}
    </>
  );
};

export default ProductView;

ProductView.propTypes = {
  product: PropTypes.object,
  purchaseOptions: PropTypes.object,
  fetchData: PropTypes.object,
  slug: PropTypes.object,
};
